import { motion } from 'motion/react';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { staggerContainer, fadeInUp } from '@/lib/animations';
import { useTranslation } from 'react-i18next';

export function CTA() {
  const { t } = useTranslation();
  const { ref, isInView } = useScrollAnimation();

  return (
    <section className="py-16 md:py-24 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 gradient-primary opacity-95" />

      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

      <Container className="relative">
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="text-center"
        >
          {/* Headline */}
          <motion.h2
            variants={fadeInUp}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6"
          >
            {t('cta.title')}
          </motion.h2>

          {/* Subheadline */}
          <motion.p
            variants={fadeInUp}
            className="text-lg sm:text-xl text-primary-100 max-w-2xl mx-auto mb-10"
          >
            {t('cta.subtitle')}
          </motion.p>

          {/* CTA buttons */}
          <motion.div
            variants={fadeInUp}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Button
              variant="secondary"
              size="lg"
              className="min-w-[200px]"
              disabled
            >
              {t('common.comingSoon')}
            </Button>
          </motion.div>

          {/* Social proof */}
          <motion.div
            variants={fadeInUp}
            className="mt-12 flex flex-wrap items-center justify-center gap-8"
          >
            <div className="text-center">
              <p className="text-3xl font-bold text-white">75%</p>
              <p className="text-primary-200 text-sm">{t('cta.stats.lessTimeSearching')}</p>
            </div>
            <div className="hidden sm:block w-px h-12 bg-white/20" />
            <div className="text-center">
              <p className="text-3xl font-bold text-white">100%</p>
              <p className="text-primary-200 text-sm">{t('cta.stats.activityVisibility')}</p>
            </div>
            <div className="hidden sm:block w-px h-12 bg-white/20" />
            <div className="text-center">
              <p className="text-3xl font-bold text-white">2 min</p>
              <p className="text-primary-200 text-sm">{t('cta.stats.createPortal')}</p>
            </div>
          </motion.div>
        </motion.div>
      </Container>
    </section>
  );
}
